import { useEffect, useRef, useState } from 'react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useToast } from '@/hooks/useToast';
import { useAppContext } from '@/hooks/useAppContext';
import { getRelay } from '@/lib/relayPool';
import {
  buildReportsData,
  buildVideoReportTags,
  EMPTY_REPORTS,
  type ReportsData,
  type ReportType,
} from '@/lib/reportUtils';
import type { VideoData } from '@/lib/videoTypes';

const REPORT_STREAM_LIMIT = 500;
const EOSE_TIMEOUT_MS = 8000;

/**
 * Stream kind 1984 report events from all read relays and build aggregate
 * report data. Reported videos are hidden by default (see useVideoFeedFilter).
 */
export function useReportsStream(enabled: boolean): ReportsData {
  const { config } = useAppContext();
  const [reports, setReports] = useState<ReportsData>(EMPTY_REPORTS);
  const accRef = useRef(new Map<string, Parameters<typeof buildReportsData>[0][number]>());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const readRelays = config.relayMetadata.relays.filter(r => r.read).map(r => r.url);
  const relayKey = readRelays.join(',');

  useEffect(() => {
    if (!enabled || readRelays.length === 0) {
      setReports(EMPTY_REPORTS);
      return;
    }

    accRef.current = new Map();
    setReports(EMPTY_REPORTS);

    const controller = new AbortController();

    const scheduleFlush = () => {
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        setReports(buildReportsData(Array.from(accRef.current.values())));
      }, 300);
    };

    for (const url of readRelays) {
      const relay = getRelay(url);

      (async () => {
        // Per-relay lifetime: ends at EOSE, abort, or after the timeout.
        const relaySignal = AbortSignal.any([
          controller.signal,
          AbortSignal.timeout(EOSE_TIMEOUT_MS * 3),
        ]);
        try {
          for await (const msg of relay.req(
            [{ kinds: [1984], limit: REPORT_STREAM_LIMIT }],
            { signal: relaySignal },
          )) {
            if (msg[0] === 'EOSE') {
              // Close after EOSE — reports don't need realtime updates for
              // filtering purposes.
              break;
            } else if (msg[0] === 'EVENT') {
              const event = msg[2];
              if (!accRef.current.has(event.id)) {
                accRef.current.set(event.id, event);
                scheduleFlush();
              }
            }
          }
        } catch {
          // ignore relay failures
        }
      })();
    }

    return () => {
      controller.abort();
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relayKey, enabled]);

  return reports;
}

/** Publish a kind 1984 report for a video. */
export function useReportVideo() {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const { toast } = useToast();

  return async (video: VideoData, type: ReportType, reason: string) => {
    if (!user) throw new Error('You must be logged in to report content');

    await createEvent({
      kind: 1984,
      content: reason,
      tags: buildVideoReportTags(video, type),
    });

    toast({
      title: 'Report submitted',
      description: 'Thank you for helping keep Nostr safe.',
    });
  };
}

/** Publish a kind 1984 report for a profile. */
export function useReportProfile() {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent } = useNostrPublish();
  const { toast } = useToast();

  return async (pubkey: string, type: ReportType, reason: string) => {
    if (!user) throw new Error('You must be logged in to report content');

    await createEvent({
      kind: 1984,
      content: reason,
      tags: [['p', pubkey, '', type]],
    });

    toast({
      title: 'Report submitted',
      description: 'Thank you for helping keep Nostr safe.',
    });
  };
}
